import React, { useState, useCallback, useEffect } from "react";
import { X } from "lucide-react";
import { CreateApiKeyRequest, apiKeysApi, datasourceApi } from "api/models";
import toast from "react-hot-toast";
import { handleAxiosAndCaughtError } from "utils";
import { useTranslation } from "react-i18next";

interface AddApiKeyFormProps {
  onClose: () => void;
  onSuccess: () => void;
  workspaceId?: string;
}

const AddApiKeyForm: React.FC<AddApiKeyFormProps> = ({ onClose, onSuccess, workspaceId }) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    expire_date: "",
    permissions: [] as string[],
    domains: [] as string[],
    ip_addresses: [] as string[],
  });

  // Fetch permissions on component mount
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!workspaceId) return;
      
      setLoadingPermissions(true);
      try {
        const permissionsList = await datasourceApi.getPermissions(workspaceId);
        setPermissions(permissionsList);
      } catch (err) {
        handleAxiosAndCaughtError(err);
        // Set empty array if API fails
        setPermissions([]);
      } finally {
        setLoadingPermissions(false);
      }
    };

    fetchPermissions();
  }, [workspaceId]);

  // Validation functions
  const isValidDomain = (domain: string): boolean => {
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
    return domainRegex.test(domain) && domain.includes('.');
  };

  const isValidIP = (ip: string): boolean => {
    const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    const ipv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
    const cidrRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\/(?:[0-9]|[1-2][0-9]|3[0-2])$/;
    return ipv4Regex.test(ip) || ipv6Regex.test(ip) || cidrRegex.test(ip);
  };

  const addArrayItem = (field: 'domains' | 'ip_addresses', value: string) => {
    if (value.trim()) {
      let processedValue = value.trim();
      
      if (field === 'domains') {
        processedValue = processedValue.replace(/^https?:\/\//, '');
        processedValue = processedValue.replace(/\/$/, '');
        processedValue = processedValue.replace(/^www\./, '');
        
        if (!isValidDomain(processedValue)) {
          toast.error(t("apiKeys.validDomain"));
          return;
        }
      } else if (field === 'ip_addresses') {
        if (!isValidIP(processedValue)) {
          toast.error(t("apiKeys.validIp"));
          return;
        }
      }
      
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], processedValue]
      }));
    }
  };

  const removeArrayItem = (field: 'domains' | 'ip_addresses', index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const selectPermission = (permissionId: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: [permissionId] // Radio behavior: only one permission selected
    }));
  };

  const resetForm = useCallback(() => {
    setFormData({
      name: "",
      expire_date: "",
      permissions: [],
      domains: [],
      ip_addresses: [],
    });
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    resetForm();
  }, [onClose, resetForm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation - only name, expire_date, and permissions are required
    if (!formData.name.trim()) {
      toast.error(t("apiKeys.nameRequiredError"));
      return;
    }

    if (!formData.expire_date) {
      toast.error(t("apiKeys.expirationRequired"));
      return;
    }

    if (formData.permissions.length === 0) {
      toast.error(t("apiKeys.permissionRequired"));
      return;
    }

    setIsSubmitting(true);
    try {
      const requestData: CreateApiKeyRequest = {
        name: formData.name.trim(),
        expire_date: formData.expire_date,
        permissions: formData.permissions,
        ...(formData.domains.length > 0 && { domains: formData.domains }),
        ...(formData.ip_addresses.length > 0 && { ip_addresses: formData.ip_addresses }),
      };

      await apiKeysApi.create(workspaceId, requestData);
      toast.success(t("apiKeys.createdSuccessfully"));
      onSuccess();
      handleClose();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex min-h-[60vh] flex-col gap-4">
      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          {t("apiKeys.createNew")}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {t("apiKeys.createDescription")}
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t("apiKeys.nameRequired")}
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder={t("apiKeys.namePlaceholder")}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-700 dark:text-white"
            required
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t("apiKeys.nameDescription")}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t("apiKeys.expiresAt")}
          </label>
          <input
            type="datetime-local"
            value={formData.expire_date}
            onChange={(e) => setFormData(prev => ({ ...prev, expire_date: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-700 dark:text-white"
            required
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t("apiKeys.expiresAtDescription")}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t("apiKeys.permission")}
          </label>
          {loadingPermissions ? (
            <div className="flex items-center justify-center py-4">
              <div className="text-sm text-gray-500 dark:text-gray-400">{t("apiKeys.loadingPermissions")}</div>
            </div>
          ) : permissions.length === 0 ? (
            <div className="flex items-center justify-center py-4">
              <div className="text-sm text-gray-500 dark:text-gray-400">{t("apiKeys.noPermissionsAvailable")}</div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              {permissions.map((permission) => (
                <div key={permission} className="flex items-center gap-2 min-w-[200px]">
                  <input
                    type="radio"
                    id={`permission-${permission}`}
                    name="permission"
                    value={permission}
                    checked={formData.permissions.includes(permission)}
                    onChange={() => selectPermission(permission)}
                    className="mt-1 h-4 w-4 text-brand-600 border-gray-300 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label htmlFor={`permission-${permission}`} className="flex-1 cursor-pointer">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t("apiKeys.permission")} {permission.toLowerCase().replace(/_/g, ' ')}
                    </div>
                  </label>
                </div>
              ))}
            </div>
          )}
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t("apiKeys.selectPermission")}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t("apiKeys.allowedDomains")}
          </label>
          <div className="space-y-2">
            <div className="min-h-[40px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus-within:ring-2 focus-within:ring-brand-500 dark:bg-gray-700 dark:text-white">
              <div className="flex flex-wrap gap-2 items-center">
                {formData.domains.map((domain, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300 rounded-full text-xs"
                  >
                    <span className="max-w-[100px] truncate">{domain}</span>
                    <button
                      type="button"
                      onClick={() => removeArrayItem('domains', index)}
                      className="ml-1 hover:text-purple-600 transition-colors flex-shrink-0"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  placeholder={formData.domains.length === 0 ? t("apiKeys.domainPlaceholder") : ""}
                  className="flex-1 min-w-[100px] bg-transparent border-none outline-none text-sm"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addArrayItem('domains', e.currentTarget.value);
                      e.currentTarget.value = '';
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value.trim()) {
                      addArrayItem('domains', e.target.value);
                      e.target.value = '';
                    }
                  }}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("apiKeys.domainDescription")}
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t("apiKeys.allowedIpAddresses")}
          </label>
          <div className="space-y-2">
            <div className="min-h-[40px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus-within:ring-2 focus-within:ring-brand-500 dark:bg-gray-700 dark:text-white">
              <div className="flex flex-wrap gap-2 items-center">
                {formData.ip_addresses.map((ip, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded-full text-xs"
                  >
                    <span className="max-w-[100px] truncate">{ip}</span>
                    <button
                      type="button"
                      onClick={() => removeArrayItem('ip_addresses', index)}
                      className="ml-1 hover:text-blue-600 transition-colors flex-shrink-0"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  placeholder={formData.ip_addresses.length === 0 ? t("apiKeys.ipPlaceholder") : ""}
                  className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addArrayItem('ip_addresses', e.currentTarget.value);
                      e.currentTarget.value = '';
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value.trim()) {
                      addArrayItem('ip_addresses', e.target.value);
                      e.target.value = '';
                    }
                  }}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("apiKeys.ipDescription")}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 pt-3 sm:flex-row sm:gap-3 sm:pt-4">
        <button
          type="button"
          onClick={handleClose}
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-20 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:px-4"
        >
          {t("common.cancel")}
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !formData.name.trim() || !formData.expire_date || formData.permissions.length === 0 || loadingPermissions || permissions.length === 0}
          className="flex-1 rounded-lg bg-brand-500 px-4 py-2 font-semibold text-white transition-colors hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? t("apiKeys.creating") : t("apiKeys.createApiKey")}
        </button>
      </div>
    </form>
  );
};

export default AddApiKeyForm; 